import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Typography,
  Box,
  Paper,
  InputAdornment,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const editableFields = [
  'duration',
  'price',
  'language',
  'startTime',
  'selectedDate',
  'is_active',
];

function AdminUpdatePosts() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);

  const [selectedPost, setSelectedPost] = useState(null);
  const [formValues, setFormValues] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [postSearchTerm, setPostSearchTerm] = useState('');

  const [alert, setAlert] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionFormValues, setSessionFormValues] = useState({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/list_users_data/`);
        const sorted = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setUsers(sorted);
        setFilteredUsers(sorted);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch users');
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!userSearchTerm) {
      setFilteredUsers(users);
      return;
    }
    const lowerSearch = userSearchTerm.toLowerCase();
    setFilteredUsers(
      users.filter(
        (user) =>
          user.name.toLowerCase().includes(lowerSearch) ||
          user.surname.toLowerCase().includes(lowerSearch) ||
          (user.city && user.city.toLowerCase().includes(lowerSearch))
      )
    );
  }, [userSearchTerm, users]);

  useEffect(() => {
    if (selectedUser) {
      const fetchPosts = async () => {
        try {
          setLoading(true);
          const response = await axios.get(
            `${API_URL}/admin_list_posts/?user_id=${selectedUser}`
          );
          setPosts(response.data);
          setFilteredPosts(response.data);
          setSelectedPost(null);
          setFormValues({});
          setLoading(false);
          setPostSearchTerm('');
        } catch (err) {
          setError('Failed to fetch posts');
          setLoading(false);
        }
      };
      fetchPosts();
    } else {
      setPosts([]);
      setFilteredPosts([]);
      setSelectedPost(null);
      setFormValues({});
      setPostSearchTerm('');
    }
  }, [selectedUser]);

  useEffect(() => {
    if (!postSearchTerm) {
      setFilteredPosts(posts);
      return;
    }
    const lowerSearch = postSearchTerm.toLowerCase();
    setFilteredPosts(
      posts.filter(
        (post) =>
          (post.title && post.title.toLowerCase().includes(lowerSearch)) ||
          (post.highlightedTopic &&
            post.highlightedTopic.toLowerCase().includes(lowerSearch))
      )
    );
  }, [postSearchTerm, posts]);

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setError(null);
    setAlert(null);
  };

  const handlePostSelect = async (postId) => {
    const post = posts.find((p) => p.id === postId);
    setSelectedPost(post);

    if (post) {
      const values = {};
      editableFields.forEach((field) => {
        if (field === 'selectedDate' && post[field]) {
          values[field] = new Date(post[field]).toISOString().split('T')[0];
        } else {
          values[field] =
            typeof post[field] === 'boolean'
              ? post[field].toString()
              : post[field] || '';
        }
      });
      setFormValues(values);
    }

    setError(null);
    setAlert(null);

    if (post?.is_bundle) {
      try {
        const response = await axios.get(
          `${API_URL}/post_bundle_by_post_id/?post_id=${post.id}`
        );
        setSessions(response.data);
      } catch (err) {
        console.error('Failed to fetch sessions:', err);
        setSessions([]);
      }
    } else {
      setSessions([]);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedPost) {
      setError('Please select a post to update.');
      return;
    }

    for (const field of editableFields) {
      if (formValues[field] === undefined || formValues[field] === '') {
        setError(`Please fill the field: ${field}`);
        return;
      }
    }

    const payload = { post_id: selectedPost.id };

    editableFields.forEach((field) => {
      if (field === 'is_active') {
        payload[field] = formValues[field] === 'true';
      } else if (field === 'duration' || field === 'price') {
        payload[field] = Number(formValues[field]);
      } else {
        payload[field] = formValues[field];
      }
    });

    try {
      await axios.put(`${API_URL}/admin_update_post/`, payload);
      setAlert('Post updated successfully');
      setError(null);
    } catch (err) {
      setError('Failed to update post');
      setAlert(null);
    }
  };

  const handleSessionSubmit = async () => {
    if (!selectedSession) {
      setError('Please select a session to update.');
      return;
    }

    const payload = {};

    editableFields.forEach((field) => {
      if (field === 'is_active') {
        payload[field] = sessionFormValues[field] === 'true';
      } else if (field === 'duration' || field === 'price') {
        payload[field] = Number(sessionFormValues[field]);
      } else {
        payload[field] = sessionFormValues[field];
      }
    });

    payload.id = selectedSession.id;

    try {
      await axios.put(`${API_URL}/admin_update_post_bundle/`, payload);
      setAlert('Session updated successfully');
      setError(null);

      const refreshed = await axios.get(
        `${API_URL}/post_bundle_by_post_id/?post_id=${selectedPost.id}`
      );
      setSessions(refreshed.data);
    } catch (err) {
      setError('Failed to update session');
      setAlert(null);
    }
  };

  if (loading)
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#121212', color: '#fff', p: 4 }}>
        <CircularProgress color="inherit" />
      </Box>
    );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000ff', color: '#fff' }}>
      <Header1 />

      <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
        <Typography
          variant="h3"
          align="center"
          fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            fontWeight: 700,
            letterSpacing: 6,
            mb: 4,
          }}
        >
          Update Posts
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {alert && <Alert severity="success">{alert}</Alert>}

        <Typography sx={{ mb: 1 }}>
          Users found: {filteredUsers.length}
        </Typography>
        <TextField
          placeholder="Search users by name or city"
          variant="outlined"
          fullWidth
          value={userSearchTerm}
          onChange={(e) => setUserSearchTerm(e.target.value)}
          sx={{
            mb: 3,
            input: { color: '#eee', bgcolor: '#1e1e1e', borderRadius: 1, px: 2 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#888' }} />
              </InputAdornment>
            ),
          }}
        />

        <Paper
          elevation={4}
          sx={{
            maxHeight: 200,
            overflowY: 'auto',
            backgroundColor: '#1e1e1e',
            p: 2,
            mb: 4,
            borderRadius: 2,
          }}
        >
          {filteredUsers.length === 0 ? (
            <Typography sx={{ color: '#888' }}>No users found.</Typography>
          ) : (
            filteredUsers.map((user) => (
              <Button
                key={user.id}
                variant={selectedUser === user.id ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() => handleUserSelect(user.id)}
                sx={{
                  justifyContent: 'flex-start',
                  color: '#fff',
                  borderColor: '#888',
                  mb: 1,
                  width: '100%',
                  textTransform: 'none',
                  bgcolor: selectedUser === user.id ? '#333' : 'transparent',
                  '&:hover': {
                    bgcolor: '#2a2a2a',
                  },
                }}
              >
                {user.name} {user.surname} — {user.city || 'No city'}
              </Button>
            ))
          )}
        </Paper>

        {selectedUser && (
          <>
            <Typography sx={{ mb: 1 }}>
              Posts found: {filteredPosts.length}
            </Typography>
            <TextField
              placeholder="Search posts by title or topic"
              variant="outlined"
              fullWidth
              value={postSearchTerm}
              onChange={(e) => setPostSearchTerm(e.target.value)}
              sx={{
                mb: 3,
                input: { color: '#eee', bgcolor: '#1e1e1e', borderRadius: 1, px: 2 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#888' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Paper
              elevation={4}
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                backgroundColor: '#1e1e1e',
                p: 2,
                mb: 4,
                borderRadius: 2,
              }}
            >
              {filteredPosts.length === 0 ? (
                <Typography sx={{ color: '#888' }}>
                  No posts found for this user.
                </Typography>
              ) : (
                filteredPosts.map((post) => (
                  <Button
                    key={post.id}
                    variant={selectedPost?.id === post.id ? 'contained' : 'outlined'}
                    color="secondary"
                    onClick={() => handlePostSelect(post.id)}
                    sx={{
                      justifyContent: 'flex-start',
                      color: '#fff',
                      borderColor: '#888',
                      mb: 1,
                      width: '100%',
                      textTransform: 'none',
                      bgcolor: selectedPost?.id === post.id ? '#333' : 'transparent',
                      '&:hover': {
                        bgcolor: '#2a2a2a',
                      },
                    }}
                  >
                    {post.title} (ID: {post.id})
                  </Button>
                ))
              )}
            </Paper>
          </>
        )}

        {/* === Always show post editor === */}
        {selectedPost && (
          <>
            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
              Edit Post: <strong>{selectedPost.title}</strong>
            </Typography>

            <Grid container spacing={2}>
              {editableFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field}>
                  {field === 'is_active' ? (
                    <TextField
                      select
                      fullWidth
                      label="Active"
                      value={formValues[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{
                        style: { color: '#fff' },
                      }}
                      sx={{
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                      }}
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      variant="outlined"
                      value={formValues[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{
                        style: { color: '#fff' },
                      }}
                      sx={{
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <Button
              variant="contained"
              color="secondary"
              sx={{ mt: 3 }}
              onClick={handleSubmit}
              fullWidth
            >
              Update Post
            </Button>
          </>
        )}

        {/* === Sessions in bundle === */}
        {selectedPost?.is_bundle && sessions.length > 0 && (
          <>
            <Divider sx={{ my: 4, borderColor: '#444' }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Sessions in Bundle
            </Typography>

            <Paper
              elevation={4}
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                backgroundColor: '#1e1e1e',
                p: 2,
                mb: 4,
                borderRadius: 2,
              }}
            >
              {sessions.map((session) => (
                <Button
                  key={session.id}
                  variant={selectedSession?.id === session.id ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => {
                    setSelectedSession(session);
                    const values = {};
                    editableFields.forEach((field) => {
                      if (field === 'selectedDate' && session[field]) {
                        values[field] = new Date(session[field]).toISOString().split('T')[0];
                      } else {
                        values[field] =
                          typeof session[field] === 'boolean'
                            ? session[field].toString()
                            : session[field] || '';
                      }
                    });
                    setSessionFormValues(values);
                  }}
                  sx={{
                    justifyContent: 'flex-start',
                    color: '#fff',
                    borderColor: '#888',
                    mb: 1,
                    width: '100%',
                    textTransform: 'none',
                    bgcolor: selectedSession?.id === session.id ? '#333' : 'transparent',
                    '&:hover': {
                      bgcolor: '#2a2a2a',
                    },
                  }}
                >
                  {session.session_title || 'Untitled Session'} — {session.selectedDate}
                </Button>
              ))}
            </Paper>
          </>
        )}

        {selectedSession && (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Editing Session: <strong>{selectedSession.session_title}</strong>
            </Typography>

            <Grid container spacing={2}>
              {editableFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field}>
                  {field === 'is_active' ? (
                    <TextField
                      select
                      fullWidth
                      label="Active"
                      value={sessionFormValues[field] || ''}
                      onChange={(e) =>
                        setSessionFormValues((prev) => ({
                          ...prev,
                          [field]: e.target.value,
                        }))
                      }
                      SelectProps={{ native: true }}
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{
                        style: { color: '#fff' },
                      }}
                      sx={{
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                      }}
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </TextField>
                  ) : (
                    <TextField
                      fullWidth
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      variant="outlined"
                      value={sessionFormValues[field] || ''}
                      onChange={(e) =>
                        setSessionFormValues((prev) => ({
                          ...prev,
                          [field]: e.target.value,
                        }))
                      }
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{
                        style: { color: '#fff' },
                      }}
                      sx={{
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>

            <Button
              variant="contained"
              color="secondary"
              sx={{ mt: 3 }}
              onClick={handleSessionSubmit}
              fullWidth
            >
              Update Session
            </Button>
          </>
        )}
      </Box>

      <Footer2 />
    </Box>
  );
}

export default AdminUpdatePosts;
